/**
 * Upload API — the calls behind "Upload File" on Manage Portfolio, in the
 * order legacy makes them (UploadDownloadTemplate.vue onFileUpload ->
 * onUdfFileChanged -> fetchRowCount -> onFileChanged | initiateCostAdvice):
 *
 *   1. GET  /upload-portfolio        creates the portfolio; returns a storage
 *                                    link for the instances file + its id
 *   2. PUT  <storage link>           the instances file  (putToStorage, below)
 *   3. GET  /upload-portfolio        with udf_file: a storage link for the
 *                                    optional self-perf file
 *   4. PUT  <storage link>           the self-perf file
 *   5. GET  /get-excel-rowcount      normal or "large" file?
 *   6. POST /file-upload/validate    normal: read the file(s) -> rows
 *      POST /initiate-costAdvice     large: process in the background
 *   x. DELETE /delete-portfolio      any failure after step 1: undo
 *      (also the Delete Portfolio button — readDeleteResult)
 *
 * All mutations: each must reach the server every time, never a cache.
 *
 * The read* helpers turn a reply into its data or THROW, so the flow
 * (hooks/useUploadPortfolio) handles every kind of failure in one catch.
 *
 * Step 1's params differ by product (uploadLinkParams): CCA sends
 * `policy_engine` and `is_billing_data`, EIA sends `headroom`.
 *
 * The contract discount travels as `pricing_model_discount`
 * ({ ondemand, reserved, reserved_3yr }, 2026-10-21) in the POST bodies, and
 * as flat query keys in step 1 (see discountPart). As
 * legacy's field, it is sent ONLY when set: here "set"
 * means at least one term above 0 (discountPart) — all blank / 0 leaves it
 * out, as a blank msa_discount was. (One narrowing: legacy still sent a typed
 * 0; three zeros are now left out too, the same as all blank.)
 */
import { policyEngineFor } from '@components/cca/ccaFormRules';
import {
  isContractDiscountEmpty,
  PRICING_MODEL_DISCOUNT,
  toPricingModelDiscount,
  toUploadDiscountParams,
} from '@components/common/ContractDiscountDialog/contractDiscountRules';
import { API_ENDPOINTS } from '@services/apiEndpoints';
import { envelopeError, isFailureEnvelope } from '@services/apiMessages';
import { baseApi } from '@services/baseApi';
import { APP_MODES } from '@variant/appMode';

const { UPLOAD_LINK, EXCEL_ROW_COUNT, VALIDATE_UPLOAD, INITIATE_COST_ADVICE, DELETE_PORTFOLIO } =
  API_ENDPOINTS.MANAGE_PORTFOLIO;

/**
 * The discount part of a request when any term is set, else `{}` — spread in.
 *  - a POST body (validate, initiate): `{ pricing_model_discount }`;
 *  - `asQuery` (step 1, GET /upload-portfolio): the backend's FLAT query keys
 *    ondemand_model_discount / reserved_model_discount /
 *    reserved_3yr_model_discount (backend curl, 2026-10-21).
 */
export function discountPart(discount, { asQuery = false } = {}) {
  if (isContractDiscountEmpty(discount)) return {};
  if (asQuery) return toUploadDiscountParams(discount);
  return { [PRICING_MODEL_DISCOUNT]: toPricingModelDiscount(discount) };
}

/** What legacy sends as file_type, for both files (apiservice.js / onFileUpload). */
export const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

/** The backend's success code on these endpoints. */
const SUCCESS_CODE = 1;

/** Step 1's reply -> { uploadUrl, portfolioId }, or throw (legacy :425-431). */
export function readUploadLink(response) {
  if (!response?.presigned_url || !response?.portfolio_id) {
    throw envelopeError(response?.data ?? response, 'Failed to get the upload link.');
  }
  return { uploadUrl: response.presigned_url, portfolioId: response.portfolio_id };
}

/** Step 3's reply -> the self-perf storage link, or throw (legacy :520-522). */
export function readSelfPerfLink(response) {
  if (!response?.udf_presigned_url) {
    throw envelopeError(response?.data ?? response, 'Failed to get the Self Perf upload link.');
  }
  return response.udf_presigned_url;
}

/** Step 5's reply -> { large }, or throw (legacy fetchRowCount :704-723). */
export function readRowCount(response) {
  if (Number(response?.ErrorCode) !== SUCCESS_CODE) {
    throw envelopeError(response, 'Upload failed. Please try again.');
  }
  return { large: Boolean(response.is_large_file) };
}

/**
 * Step 6 (normal file) -> { rows, selfPerfRows, otherCloudRows }, or throw
 * (legacy onFileChanged :596-602, :605-663).
 */
export function readValidatedUpload(response) {
  if (Number(response?.ErrorCode) !== SUCCESS_CODE) {
    throw envelopeError(response, 'File upload failed. Please upload a valid file.');
  }
  const list = (value) => (Array.isArray(value) ? value : []);
  return {
    rows: list(response.Data),
    selfPerfRows: list(response.UDF),
    otherCloudRows: list(response.Other_cloud_data),
  };
}

/** Step 6 (large file) -> the server's message, or throw (legacy :741-743). */
export function readBackgroundStart(response) {
  if (response?.status !== 'success') {
    throw envelopeError(response, 'Could not start processing the file.');
  }
  return response.message || 'Cost advice initiated successfully';
}

/**
 * Steps 2 / 4: send a file to a storage link.
 *
 * A plain fetch, NOT the API client: the link is pre-signed (it carries its
 * own permission), and the app's Authorization / Appname headers would make
 * the storage service reject it. Legacy does the same (fetch PUT, :440-447).
 */
export async function putToStorage(url, file) {
  const response = await fetch(url, {
    method: 'PUT',
    headers: { 'Content-Type': XLSX_MIME },
    body: await file.arrayBuffer(),
  });
  if (!response.ok) throw new Error('File upload failed. Please try again.');
}

/**
 * Delete Portfolio reply -> the server's message, or THROW on a failure
 * envelope (ErrorCode -1). Legacy (FooterActions.vue DeletePortfolio) never
 * checks: a refused delete still showed green and cleared the page.
 */
export function readDeleteResult(response) {
  if (isFailureEnvelope(response)) {
    throw envelopeError(response, 'Error while deleting portfolio. Please try again.');
  }
  return response?.Message || response?.message || 'Deleted successfully';
}

/**
 * Step 1 / step 3 params for GET /upload-portfolio (legacy
 * UploadDownloadTemplate.vue onFileUpload :407-421, onUdfFileChanged :513-518).
 *
 * Legacy sends portfolio_id for an existing portfolio, else portfolioName.
 * By product (`mode`):
 *  - CCA: `policy_engine` (the opened portfolio's saved value, else
 *    "No Policy Engine (Default)" — legacy has no picker) and `is_billing_data`
 *    (true in Billing / CUR mode). No `headroom` — CCA has no safety margin.
 *    Legacy tied `is_billing_data` to its `fileType`, which an opened
 *    portfolio's reload could reset to 'instancesku', so a re-upload into a
 *    Billing portfolio could go up as an ordinary one; here it follows the
 *    Billing pick, on every upload, new or existing.
 *  - EIA: `headroom` (when given). No policy engine, no billing.
 * `provider` must already be a cloud — in Billing mode the Billing "Cloud"
 * pick (dashboard billingProvider), as legacy's datadogCSP.
 */
export function uploadLinkParams({
  mode,
  provider,
  fileName,
  portfolioName,
  portfolioId,
  model,
  headroom,
  discount,
  policyEngine,
  billing = false,
  udfFile,
}) {
  if (udfFile) {
    // The self-perf link takes only these three (legacy udfQueryParams).
    return { portfolio_id: portfolioId, udf_file: udfFile, file_type: XLSX_MIME };
  }
  const cca = mode === APP_MODES.CCA;
  return {
    provider,
    file_name: fileName,
    file_type: XLSX_MIME,
    ...(portfolioId ? { portfolio_id: portfolioId } : { portfolioName }),
    ...(model ? { model } : {}),
    ...(cca
      ? { policy_engine: policyEngineFor(policyEngine), is_billing_data: Boolean(billing) }
      : {}),
    ...(!cca && headroom !== undefined ? { headroom } : {}),
    ...discountPart(discount, { asQuery: true }),
  };
}

export const uploadApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    /** Step 1 (instances file) and step 3 (self-perf file, with udfFile). */
    getUploadLink: builder.mutation({
      query: (args) => ({ url: UPLOAD_LINK, params: uploadLinkParams(args) }),
    }),

    getExcelRowCount: builder.mutation({
      query: (portfolioId) => ({ url: EXCEL_ROW_COUNT, params: { portfolio_id: portfolioId } }),
    }),

    validateUpload: builder.mutation({
      query: ({ portfolioId, discount }) => ({
        url: VALIDATE_UPLOAD,
        method: 'POST',
        body: {
          portfolio_id: portfolioId,
          ...discountPart(discount),
        },
      }),
      // A new portfolio now exists — the sidebar list must show it (legacy
      // re-fetches the list here, onFileChanged -> getPortfolios).
      invalidatesTags: ['Portfolio'],
    }),

    initiateCostAdvice: builder.mutation({
      query: ({ portfolioId, discount }) => ({
        url: INITIATE_COST_ADVICE,
        method: 'POST',
        body: {
          portfolio_id: portfolioId,
          ...discountPart(discount),
        },
      }),
      invalidatesTags: ['Portfolio'],
    }),

    deletePortfolio: builder.mutation({
      query: (portfolioId) => ({
        url: DELETE_PORTFOLIO,
        method: 'DELETE',
        params: { id: portfolioId },
      }),
      invalidatesTags: ['Portfolio'],
    }),
  }),
});

export const {
  useGetUploadLinkMutation,
  useGetExcelRowCountMutation,
  useValidateUploadMutation,
  useInitiateCostAdviceMutation,
  useDeletePortfolioMutation,
} = uploadApi;
